import { useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { searchRegisteredStudents, RegisteredStudent } from '../api/staffStudents';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';

interface StudentSearchAutocompleteProps {
  onSelect: (student: RegisteredStudent | null) => void;
  disabled?: boolean;
}

export default function StudentSearchAutocomplete({
  onSelect,
  disabled = false,
}: StudentSearchAutocompleteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Search for students when search query changes
  const { data, isLoading } = useQuery({
    queryKey: ['studentSearch', searchQuery],
    queryFn: () => searchRegisteredStudents(searchQuery),
    enabled: searchQuery.length >= 2, // Only search with 2+ characters
    staleTime: 30000, // Cache results for 30 seconds
  });

  const students = data || [];

  return (
    <Autocomplete
      disabled={disabled}
      options={students}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        // Update search query only if we have enough characters
        if (newInputValue.length >= 2) {
          setSearchQuery(newInputValue);
        } else {
          setSearchQuery('');
        }
      }}
      onChange={(_, newValue) => {
        onSelect(newValue);
      }}
      getOptionLabel={(option) =>
        `${option.first_name} ${option.last_name} (${option.grade})`
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search for registered student"
          placeholder="Type student name..."
          helperText="Type at least 2 characters to search"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <PersonIcon color="primary" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" fontWeight={600}>
                {option.first_name} {option.last_name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Chip
                  icon={<SchoolIcon />}
                  label={`Grade ${option.grade}`}
                  size="small"
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {option.family.family_name}
                </Typography>
              </Box>
              {option.medical_notes && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                  ⚠️ Medical: {option.medical_notes}
                </Typography>
              )}
            </Box>
            <Chip
              label={option.pickup_type === 'car' ? 'Car' : 'Walker'}
              size="small"
              color={option.pickup_type === 'car' ? 'primary' : 'secondary'}
            />
          </Box>
        </Box>
      )}
      noOptionsText={
        searchQuery.length < 2
          ? 'Type at least 2 characters to search'
          : 'No students found'
      }
    />
  );
}
